﻿import { decodeBoatClass } from '@/network/decoders'
import EventBoat from '@model/EventBoat'
import EventBoatRace from '@model/EventBoatRace'
import EventCategory from '@model/EventCategory'
import EventClass from '@model/EventClass'
import EventCourse from '@model/EventCourse'
import EventCourseObject from '@model/EventCourseObject'
import EventCrew from '@model/EventCrew'
import EventHeatResult from '@model/EventHeatResult'
import EventLifeguard from '@model/EventLifeguard'
import EventRace from '@model/EventRace'
import EventRaceDetailedClass from "@model/EventRaceDetailedClass"
import EventRaceSkippedClass from '@model/EventRaceSkippedClass'
import EventReferee from '@model/EventReferee'
import EventResult from '@model/EventResult'
import EventRound from '@model/EventRound'
import EventsRepo from '@repo/EventsRepo'
import { useRepo } from 'pinia-orm'

export async function createEventFromFile ( content ) {
	const storedEvent = useRepo( EventsRepo ).createEvent( {
		title: content.event.title,
		ctlId: parseInt( content.event.ctl ),
		from: content.event.from.toDate(),
		to: content.event.to === null ? null : content.event.to.toDate(),
		venueId: content.event.venueId,
		clubId: content.event.clubId,
		organizer: content.event.organizer,
		otherVenue: content.event.otherVenue,
		registrationUrl: content.event.registrationUrl,
		noticeboardUrl: content.event.noticeboardUrl,
		description: content.event.description,
		sponsor: content.event.sponsor,
		directorId: content.event.directorId ?? null,
		director: content.event.director ?? content.event.otherDirector ?? null,
		guarantorId: content.event.guarantorId ?? null,
		plannedRaces: content.event.plannedRaces,
		created: content.event.created.toDate(),
		noLifeguards: content.event.noLifeguards,
		rrsUuid: content.event.rrsUuid,
		ignoreUpdateMembers: content.event.ignoreUpdateMembers,
		forceEnglishReports: content.event.forceEnglishReports ?? false,
		descInResultsTitle: content.event.descInResultsTitle,
		crewDescInResultsTitle: content.event.crewDescInResultsTitle,
		wsEventId: content.event.wsEventId
	} )

	useRepo( EventsRepo ).save( storedEvent )

	if ( content.event.courses ) {
		const coursesToSave = content.event.courses.map( ( eventCourse ) => ( {
			eventId: storedEvent.id,
			title: eventCourse.title,
			_originalObjects: eventCourse.objects
		} ) )

		const storedCourses = useRepo( EventCourse ).save( coursesToSave )
		const coursesArray = Array.isArray( storedCourses ) ? storedCourses : [ storedCourses ]

		const objectsToSave = []
		coursesArray.forEach( ( storedCourse, index ) => {
			const originalObjects = coursesToSave[ index ]._originalObjects
			originalObjects?.forEach( ( obj ) => {
				objectsToSave.push( {
					eventId: storedEvent.id,
					eventCourseId: storedCourse.id,
					type: ( obj.type === 'start' || obj.type === 'finish' ) ? CourseObjectType.RaceLine : obj.type,
					x: obj.x,
					y: obj.y,
					x2: obj.x2 ?? 0,
					y2: obj.y2 ?? 0,
					x3: obj.x3 ?? 0,
					y3: obj.y3 ?? 0,
					label: obj.label,
					secondLabel: obj.secondLabel ?? '',
					secondRaceline: obj.secondRaceline ?? false,
					sharedRaceline: obj.sharedRaceline ?? false,
					reversedDirection: obj.reversedDirection ?? false,
					secondReversedDirection: obj.secondReversedDirection ?? false,
					rotation: obj.rotation,
					color: obj.color ?? 'white',
					secondColor: obj.secondColor ?? 'white',
					thirdColor: obj.thirdColor ?? 'white',
					shape: obj.shape ?? 'sphere',
					secondShape: obj.secondShape ?? 'sphere',
					direction: obj.direction ?? 'left',
					showDirection: obj.showDirection ?? true,
					nextObjectId: obj.nextObjectId ?? null,
					nextObjectIdSecondary: obj.nextObjectIdSecondary ?? false,
					prevObjectId: obj.prevObjectId ?? null,
					prevObjectIdSecondary: obj.prevObjectIdSecondary ?? false,
					colorFlag1: obj.colorFlag1 ?? 'none',
					colorFlag2: obj.colorFlag2 ?? 'none'
				} )
			} )
		} )

		if ( objectsToSave.length > 0 ) {
			useRepo( EventCourseObject ).save( objectsToSave )
		}
	}

	const categoriesToSave = []
	const classesToSave = content.event.classes.map( ( eventClass ) => ( {
		eventId: storedEvent.id,
		boatClassId: decodeBoatClass( eventClass.boatClassId ),
		weight: eventClass.weight,
		eventType: eventClass.eventType,
		shortcut: eventClass.shortcut,
		discardDefinition: eventClass.discardDefinition,
		className: ( eventClass.className !== null && eventClass.className !== undefined ) ? eventClass.className : null,
		publicLicenseExceptionType: ( eventClass.publicLicenseExceptionType !== null && eventClass.publicLicenseExceptionType !== undefined ) ? eventClass.publicLicenseExceptionType : 0,
		days: ( eventClass.days !== null && eventClass.days !== undefined ) ? eventClass.days : null,
		startDate: ( eventClass.startDate !== null && eventClass.startDate !== undefined ) ? eventClass.startDate.toDate() : null,
		handicapSystem: ( eventClass.handicapSystem !== null && eventClass.handicapSystem !== undefined ) ? eventClass.handicapSystem : null,
		raceMode: ( eventClass.raceMode !== null && eventClass.raceMode !== undefined ) ? eventClass.raceMode : null,
		eliminationHeats: eventClass.eliminationHeats ?? 2,
		eliminationRounds: eventClass.eliminationRounds ?? 2,
		eliminationStartSortMode: eventClass.eliminationStartSortMode ?? 'sailNo',
		eliminationStartDivisionMode: eventClass.eliminationStartDivisionMode ?? 'blocks',
		eliminationRoundDistributionMode: eventClass.eliminationRoundDistributionMode ?? 'lessFirst',
		eliminationHeatSizes: eventClass.eliminationHeatSizes ?? '',
		eliminationRaceDistributionMode: eventClass.eliminationRaceDistributionMode ?? 'snake',
		eliminationUseFinalGoldenNames: eventClass.eliminationUseFinalGoldenNames ?? true,
		eliminationHeatIdentStyle: eventClass.eliminationHeatIdentStyle ?? 'ordered',
		bestTimeResults: eventClass.bestTimeResults ?? false,
		racePenaltyTime: eventClass.racePenaltyTime ?? 10,
		isLongDistance: eventClass.isLongDistance ?? false,
		_originalCategories: eventClass.categories
	} ) )

	const storedClasses = useRepo( EventClass ).save( classesToSave )
	const classesArray = Array.isArray( storedClasses ) ? storedClasses : [ storedClasses ]

	classesArray.forEach( ( storedClass, index ) => {
		const originalCategories = classesToSave[ index ]._originalCategories
		originalCategories?.forEach( ( category ) => {
			categoriesToSave.push( {
				eventId: storedEvent.id,
				eventClassId: storedClass.id,
				shortcut: category.shortcut,
				title: category.title,
				sex: category.sex,
				ageUnder: category.ageUnder,
				ageAbove: category.ageAbove,
				otherLimit: category.otherLimit,
			} )
		} )
	} )
	if ( categoriesToSave.length > 0 ) {
		useRepo( EventCategory ).save( categoriesToSave )
	}

	const coursesMap = new Map( useRepo( EventCourse ).where( 'eventId', storedEvent.id ).get().map( c => [ c.title, c.id ] ) )
	const refereesToSave = content.event.referees.map( ( referee ) => ( {
		eventId: storedEvent.id,
		refereeId: referee.refereeId,
		refereeRoleId: referee.refereeRoleId,
		wsId: referee.wsId,
		scorer: referee.scorer ?? false,
		courseId: coursesMap.get( referee.course ),
		foreignerFirstName: referee.foreignerFirstName,
		foreignerLastName: referee.foreignerLastName,
		foreignerSex: referee.foreignerSex,
		foreignerEmail: referee.foreignerEmail,
		foreignerPhone: referee.foreignerPhone
	} ) )
	useRepo( EventReferee ).save( refereesToSave )

	const lifeguardsToSave = content.event.lifeguards.map( ( lifeguard ) => ( {
		eventId: storedEvent.id,
		lifeguardId: lifeguard.lifeguardId,
		lifeguardRoleId: lifeguard.lifeguardRoleId,
	} ) )
	useRepo( EventLifeguard ).save( lifeguardsToSave )

	const boatsToMap = new Map<string, string>
	const crewToSave = []
	const boatsToSave = content.event.boats.map( ( boat ) => ( {
		eventId: storedEvent.id,
		boatClassId: decodeBoatClass( boat.boatClassId ),
		country: boat.country,
		sailNumber: boat.sailNumber,
		name: boat.name,
		detail: boat.detail,
		paid: boat?.paid ?? 0,
		handicapSelected: boat?.handicapSelected ?? null,
		handicapValue: boat?.handicapValue ?? null,
		_originalBoatId: boat.id,
		_originalCrew: boat.crew
	} ) )

	const storedBoats = useRepo( EventBoat ).save( boatsToSave )
	const boatsArray = Array.isArray( storedBoats ) ? storedBoats : [ storedBoats ]

	boatsArray.forEach( ( storedBoat, index ) => {
		const boatData = boatsToSave[ index ]
		boatsToMap.set( boatData._originalBoatId, storedBoat.id )

		boatData._originalCrew.forEach( ( crew ) => {
			crewToSave.push( {
				eventId: storedEvent.id,
				eventBoatId: storedBoat.id,
				memberId: crew.memberId,
				captain: crew.captain,
				order: crew.order,
				nonMemberRegId: crew.nonMemberRegId,
				nonMemberFirstName: crew.nonMemberFirstName,
				nonMemberLastName: crew.nonMemberLastName,
				nonMemberSex: crew.nonMemberSex,
				nonMemberBirthDate: crew.nonMemberBirthDate === null ? null : crew.nonMemberBirthDate.toDate(),
				nonMemberBirthCode: crew.nonMemberBirthCode,
				agedBirthYear: crew.agedBirthYear ?? null,
				nonMemberCitizenship: crew.nonMemberCitizenship,
				nonMemberZip: crew.nonMemberZip,
				nonMemberStreet: crew.nonMemberStreet ?? crew.nonMemberAddress,
				nonMemberCity: crew.nonMemberCity,
				nonMemberHouseNumber: crew.nonMemberHouseNumber,
				nonMemberOrientationNumber: crew.nonMemberOrientationNumber,
				nonMemberRegistrationNumber: crew.nonMemberRegistrationNumber,
				nonMemberEmail: crew.nonMemberEmail,
				nonMemberPhone: crew.nonMemberPhone,
				note: crew.note,
				wsId: crew.wsId
			} )
		} )
	} )
	if ( crewToSave.length > 0 ) {
		useRepo( EventCrew ).save( crewToSave )
	}

	const skippedClassesToSave = []
	const detailedClassesToSave = []
	const eventBoatRacesToSave = []
	const resultsToSave = []
	const roundsToSave = []
	const heatResultsToSave = []

	content.event.races.forEach( function ( race ) {
		const eventRace = useRepo( EventRace ).save( {
			eventId: storedEvent.id,
			race: race.race,
		} )

		race.skippedClasses.forEach( function ( skipClass ) {
			skippedClassesToSave.push( {
				eventRaceId: eventRace.id,
				boatClassId: decodeBoatClass( skipClass.boatClassId ),
			} )
		} )

		if ( race.detailedClasses !== undefined )
			race.detailedClasses.forEach( function ( detailClass ) {
				detailedClassesToSave.push( {
					eventRaceId: eventRace.id,
					boatClassId: decodeBoatClass( detailClass.boatClassId ),
					startTime: detailClass.startTime === null ? null : detailClass.startTime.toDate(),
					specialBehavior: detailClass.specialBehavior ?? null
				} )
			} )

		if ( race.eventBoatRaces !== undefined )
			race.eventBoatRaces.forEach( function ( boatRace ) {
				eventBoatRacesToSave.push( {
					eventBoatId: boatsToMap.get( boatRace.eventBoatId ),
					eventRaceId: eventRace.id,
					handicapSelected: boatRace.handicapSelected,
					handicapValue: boatRace.handicapValue,
				} )
			} )

		race.results.forEach( function ( result ) {
			resultsToSave.push( {
				eventId: storedEvent.id,
				eventBoatId: boatsToMap.get( result.eventBoatId ),
				eventRaceId: eventRace.id,
				order: result.order,
				other: result.other,
				attr: result.attr,
				fixedPoints: result.fixedPoints ?? null,
				counter: result.counter ?? 0,
				rounds: result.rounds,
				finishTime: ( result.finishTime === undefined || result.finishTime === null ) ? null : result.finishTime.toDate(),
				ts: ( result.ts === null || result.ts === undefined ) ? null : result.ts.toDate()
			} )
		} )

		if ( race.rounds !== undefined )
			race.rounds.forEach( function ( round ) {
				roundsToSave.push( {
					eventId: storedEvent.id,
					eventBoatId: boatsToMap.get( round.eventBoatId ),
					eventRaceId: eventRace.id,
					round: round.id ?? 0,
					counter: round.counter ?? 0,
					ts: ( round.ts === null || round.ts === undefined ) ? null : round.ts.toDate()
				} )
			} )

		if ( race.heatResults !== undefined )
			race.heatResults.forEach( function ( heatResult ) {
				heatResultsToSave.push( {
					eventId: storedEvent.id,
					eventBoatId: boatsToMap.get( heatResult.eventBoatId ),
					eventRaceId: eventRace.id,
					round: heatResult.round ?? 0,
					heat: heatResult.heat ?? 0,
					order: heatResult.order,
					other: heatResult.other,
					attr: heatResult.attr,
					ts: ( heatResult.ts === null || heatResult.ts === undefined ) ? null : heatResult.ts.toDate()
				} )
			} )
	} )

	if ( skippedClassesToSave.length > 0 ) useRepo( EventRaceSkippedClass ).save( skippedClassesToSave )
	if ( detailedClassesToSave.length > 0 ) useRepo( EventRaceDetailedClass ).save( detailedClassesToSave )
	if ( eventBoatRacesToSave.length > 0 ) useRepo( EventBoatRace ).save( eventBoatRacesToSave )
	if ( resultsToSave.length > 0 ) useRepo( EventResult ).save( resultsToSave )
	if ( roundsToSave.length > 0 ) useRepo( EventRound ).save( roundsToSave )
	if ( heatResultsToSave.length > 0 ) useRepo( EventHeatResult ).save( heatResultsToSave )

	return storedEvent.id
}
